import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginInput, LoginResponse } from '@amzjp/shared';
import { api, setAccessToken, setUnauthorizedHandler } from '@/lib/api';

export type AuthUser = LoginResponse['user'];

interface AuthContextValue {
  user: AuthUser | null;
  /** İlk açılışta mevcut oturum sınanırken true. */
  initializing: boolean;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Açılıştaki oturum sınaması tek sefer yapılır.
 *
 * React StrictMode geliştirmede efektleri çift çalıştırır; bu iki `/auth/refresh`
 * isteği demektir ve rotasyonlu token'da yarışa yol açar. Sunucu tarafında tolerans
 * penceresi var (§23.1) ama gereksiz isteği baştan engellemek daha doğru.
 */
let bootstrapPromise: Promise<LoginResponse> | null = null;

function bootstrapSession(): Promise<LoginResponse> {
  bootstrapPromise ??= api
    .post<LoginResponse>('/auth/refresh', undefined, { skipRefresh: true })
    .finally(() => {
      bootstrapPromise = null;
    });
  return bootstrapPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  // Sayfa yenilendiğinde access token bellekte olmaz; httpOnly refresh cookie'siyle
  // sessizce yeni bir oturum alınmaya çalışılır.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const data = await bootstrapSession();
        if (cancelled) return;
        setAccessToken(data.accessToken);
        setUser(data.user);
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  // Token yenilenemediğinde API istemcisi buradan oturumu düşürür
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  const login = useCallback(async (input: LoginInput) => {
    const data = await api.post<LoginResponse>('/auth/login', input, { skipRefresh: true });
    setAccessToken(data.accessToken);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Sunucu hata verse de yerel oturumu kapatırız
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const me = await api.get<AuthUser>('/auth/me');
    setUser(me);
  }, []);

  const value = useMemo(
    () => ({ user, initializing, login, logout, refreshUser }),
    [user, initializing, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth, AuthProvider içinde kullanılmalı');
  return context;
}
